// The client camera (architecture §9.5, mechanics §14.3–14.4, movement.md §1.5–1.6, §4.5):
// position and zoom easing, wheel zoom, the observer camera, and screen ↔ world transforms.
// - Playing: eases (half-life 28 ms: one third of the gap per 60 Hz frame, agar's measured
//   follow) toward the r²-weighted centroid of the own cells' interpolated centres (the same
//   centroid as `store.ownNewest()`, so the steering anchor and the screen agree); zoom eases
//   (half-life 145 ms, τ ≈ 207 ms) toward `cameraZoom(viewScale(Σr), W, H, wheelZoom)`.
// - No cells: eases (500 ms, position and zoom) toward the server's CAMERA (centre and
//   viewScale); with none yet, holds.
// - Death hold (visuals §10, mechanics §15): when the last own cell disappears (death,
//   cash-out), the view stays where it was drawn (the last own centroid and view scale, with the
//   own-cell half-lives) instead of easing toward the server's death camera, which sits about
//   v·(interpolation delay) ahead of what was on screen. The hold ends with the next own cell,
//   `releaseHold()` (Spectate) or `reset()`. Wheel zoom still applies during the hold.
// - Easing is frame-rate independent (`easeFactor(dtMs, halfLife)` from @ballio/shared).
// - The first target after construction or `reset()` is snapped to (no slow glide in from the
//   map centre on boot or after a room change); `snap()` does the same on demand.
//
// Implementer: client net/game. Consumers: app.ts main loop (`update`), render (transform,
// `visibleRect` culling), input (`screenToWorld`, wheel), ui (party arrows, M3). DOM-free.

import {
  applyWheelNotches,
  CAMERA_EASE,
  type CameraSection,
  cameraZoom,
  clamp,
  easeFactor,
  type Vec2,
  viewScale,
  WHEEL_ZOOM,
} from '@ballio/shared';

/** World-space rectangle (inclusive bounds). */
export interface WorldRect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Anything with a centre and radius (EntityRecord qualifies). */
export interface CameraTarget {
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

/**
 * Own-cell follow half-lives (movement.md §1.5–1.6, measured on agar): position 28 ms (k ≈ 1/3 per
 * 60 Hz frame, steady lag v·33 ms), zoom 145 ms (= CAMERA_EASE in @ballio/shared).
 */
export const CAMERA_FOLLOW = Object.freeze({
  positionHalfLifeMs: CAMERA_EASE.positionHalfLifeMs,
  zoomHalfLifeMs: CAMERA_EASE.zoomHalfLifeMs,
});

/** Smallest zoom used by the transforms (guards a zero-size viewport). */
const MIN_ZOOM = 1e-4;

export class Camera {
  /** World point at the viewport centre. */
  x = 0;
  y = 0;
  /** CSS px per world unit. */
  zoom = 1;
  /** Wheel zoom in [0.8, 4] (WHEEL_ZOOM); kept between lives, reset on page load. */
  wheelZoom = WHEEL_ZOOM.default;
  /** Viewport size in CSS px (from the last update). */
  width = 0;
  height = 0;
  /** The view scale `s` the zoom target was computed from in the last update (1 before). */
  scale = 1;
  /** False until the first target was applied (it is snapped to, not eased). */
  private placed = false;
  /** The last own-cell target (centroid and view scale) and whether the last update had cells. */
  private lastOwnX = 0;
  private lastOwnY = 0;
  private lastOwnS = 1;
  private hadOwn = false;
  /** The death hold is on (see the header). */
  private holding = false;

  /** The view is held at the last own-cell position (after a death or cash-out). */
  get isHolding(): boolean {
    return this.holding;
  }

  /** Ends the death hold: the server's CAMERA is followed again (Spectate). */
  releaseHold(): void {
    this.holding = false;
  }

  /**
   * One display frame. `own` = the visible own cells (store.view.own); `observer` = the last
   * CAMERA section (used only when `own` is empty); viewport in CSS px.
   */
  update(
    dtMs: number,
    own: readonly CameraTarget[],
    observer: CameraSection | null,
    viewportW: number,
    viewportH: number,
  ): void {
    this.width = viewportW > 0 ? viewportW : 0;
    this.height = viewportH > 0 ? viewportH : 0;
    const dt = dtMs > 0 && Number.isFinite(dtMs) ? dtMs : 0;

    let tx: number;
    let ty: number;
    let s: number;
    let posHalfLife: number;
    let zoomHalfLife: number;
    if (own.length > 0) {
      let sx = 0;
      let sy = 0;
      let sr = 0;
      let sw = 0;
      let mx = 0;
      let my = 0;
      for (let i = 0; i < own.length; i++) {
        const c = own[i] as CameraTarget;
        const w = c.r * c.r;
        sx += c.x * w;
        sy += c.y * w;
        sw += w;
        mx += c.x;
        my += c.y;
        sr += c.r;
      }
      // r²-weighted (backlog C-3); the plain mean only when every radius is 0.
      tx = sw > 0 ? sx / sw : mx / own.length;
      ty = sw > 0 ? sy / sw : my / own.length;
      s = viewScale(sr);
      posHalfLife = CAMERA_FOLLOW.positionHalfLifeMs;
      zoomHalfLife = CAMERA_FOLLOW.zoomHalfLifeMs;
      if (Number.isFinite(tx) && Number.isFinite(ty) && Number.isFinite(s)) {
        this.lastOwnX = tx;
        this.lastOwnY = ty;
        this.lastOwnS = s;
        this.hadOwn = true;
        this.holding = false;
      }
    } else if (this.hadOwn || this.holding) {
      if (this.hadOwn) {
        this.hadOwn = false;
        this.holding = true;
      }
      tx = this.lastOwnX;
      ty = this.lastOwnY;
      s = this.lastOwnS;
      posHalfLife = CAMERA_FOLLOW.positionHalfLifeMs;
      zoomHalfLife = CAMERA_FOLLOW.zoomHalfLifeMs;
    } else if (observer) {
      tx = observer.x;
      ty = observer.y;
      s = observer.viewScale > 0 ? observer.viewScale : 1;
      posHalfLife = CAMERA_EASE.observerHalfLifeMs;
      zoomHalfLife = CAMERA_EASE.observerHalfLifeMs;
    } else {
      return;
    }
    if (!Number.isFinite(tx) || !Number.isFinite(ty) || !Number.isFinite(s)) return;
    this.scale = s;

    const hasViewport = this.width > 0 && this.height > 0;
    const tz = hasViewport ? cameraZoom(s, this.width, this.height, this.wheelZoom) : this.zoom;

    if (!this.placed) {
      this.x = tx;
      this.y = ty;
      if (hasViewport) this.zoom = tz;
      this.placed = true;
      return;
    }
    const kp = easeFactor(dt, posHalfLife);
    this.x += (tx - this.x) * kp;
    this.y += (ty - this.y) * kp;
    if (hasViewport) {
      const kz = easeFactor(dt, zoomHalfLife);
      this.zoom += (tz - this.zoom) * kz;
    }
  }

  /** Jump without easing (first frame of a room, respawn). */
  snap(x: number, y: number, zoom?: number): void {
    if (Number.isFinite(x)) this.x = x;
    if (Number.isFinite(y)) this.y = y;
    if (zoom !== undefined && zoom > 0 && Number.isFinite(zoom)) this.zoom = zoom;
    this.placed = true;
  }

  /** The next `update` with a target snaps to it (new room). Keeps `wheelZoom`. */
  reset(): void {
    this.placed = false;
    this.hadOwn = false;
    this.holding = false;
  }

  /** Wheel notches: +1 = zoom in ×1.1, −1 = out (clamped to [0.8, 4]). */
  zoomBy(notches: number): void {
    if (!Number.isFinite(notches) || notches === 0) return;
    this.wheelZoom = applyWheelNotches(this.wheelZoom, notches);
  }

  /** Multiplies the wheel zoom by `factor` (pinch zoom), clamped to [0.8, 4]. */
  zoomByFactor(factor: number): void {
    if (!(factor > 0) || !Number.isFinite(factor)) return;
    this.wheelZoom = clamp(this.wheelZoom * factor, WHEEL_ZOOM.min, WHEEL_ZOOM.max);
  }

  /** World → CSS px relative to the canvas's top-left corner. */
  worldToScreen(wx: number, wy: number, out: Vec2): Vec2 {
    const z = this.zoom > MIN_ZOOM ? this.zoom : MIN_ZOOM;
    out.x = (wx - this.x) * z + this.width / 2;
    out.y = (wy - this.y) * z + this.height / 2;
    return out;
  }

  /** CSS px → world: `(p − screenCentre) / zoom + cameraCentre` (protocol §8.5). */
  screenToWorld(sx: number, sy: number, out: Vec2): Vec2 {
    const z = this.zoom > MIN_ZOOM ? this.zoom : MIN_ZOOM;
    out.x = (sx - this.width / 2) / z + this.x;
    out.y = (sy - this.height / 2) / z + this.y;
    return out;
  }

  /** The world rectangle on screen, grown by `marginPx` screen pixels on every side (culling). */
  visibleRect(out: WorldRect, marginPx = 0): WorldRect {
    const z = this.zoom > MIN_ZOOM ? this.zoom : MIN_ZOOM;
    const hw = (this.width / 2 + marginPx) / z;
    const hh = (this.height / 2 + marginPx) / z;
    out.minX = this.x - hw;
    out.minY = this.y - hh;
    out.maxX = this.x + hw;
    out.maxY = this.y + hh;
    return out;
  }
}
